// Mirrors server/src/domain/form.ts — keep the two in sync.

export const QUESTION_TYPES = [
  'short_text',
  'long_text',
  'email',
  'phone',
  'number',
  'date',
  'single_choice',
  'multiple_choice',
  'dropdown',
  'file_upload',
] as const;

export type QuestionType = (typeof QUESTION_TYPES)[number];

export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  short_text: 'Short answer',
  long_text: 'Paragraph',
  email: 'Email',
  phone: 'Phone',
  number: 'Number',
  date: 'Date',
  single_choice: 'Multiple choice (one answer)',
  multiple_choice: 'Checkboxes (several answers)',
  dropdown: 'Dropdown',
  file_upload: 'File / image upload',
};

/** One-line guidance shown when picking a type in the template editor. */
export const QUESTION_TYPE_HINTS: Record<QuestionType, string> = {
  short_text: 'Names, links, one-line answers',
  long_text: 'Explanations and stories',
  email: 'Checks the address format',
  phone: 'Checks the number format',
  number: 'Quantities and amounts',
  date: 'Calendar picker',
  single_choice: 'Pick one, all options visible',
  multiple_choice: 'Pick any number of options',
  dropdown: 'Pick one from a long list',
  file_upload: 'Images or PDF files',
};

export const CHOICE_TYPES: ReadonlySet<QuestionType> = new Set([
  'single_choice',
  'multiple_choice',
  'dropdown',
]);

export interface Question {
  id: string;
  type: QuestionType;
  label: string;
  description?: string;
  quote?: string;
  placeholder?: string;
  required: boolean;
  options?: string[];
  imageId?: string;
}

export interface Section {
  id: string;
  title: string;
  description?: string;
  imageId?: string;
  questions: Question[];
}

export interface FormDefinition {
  name: string;
  description?: string;
  logoImageId?: string;
  sections: Section[];
}

export interface Template extends FormDefinition {
  id: string;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateSummary {
  id: string;
  name: string;
  description?: string;
  logoImageId?: string;
  sectionCount: number;
  questionCount: number;
  linkCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface FileRef {
  id: string;
  name: string;
  contentType: string;
  size: number;
}

export type AnswerValue = string | number | string[] | FileRef[];
export type Answers = Record<string, AnswerValue>;

export type ClientFormStatus = 'pending' | 'in_progress' | 'submitted';

export interface ClientFormSummary {
  id: string;
  slug: string;
  url: string;
  clientName: string;
  clientEmail?: string;
  templateId: string;
  templateName: string;
  status: ClientFormStatus;
  isActive: boolean;
  answeredCount: number;
  questionCount: number;
  createdAt: string;
  firstOpenedAt?: string;
  lastSavedAt?: string;
  submittedAt?: string;
  lastSentAt?: string;
}

export interface ClientFormDetail extends ClientFormSummary {
  notes?: string;
  form: FormDefinition;
  answers: Answers;
}

export interface PublicForm {
  slug: string;
  clientName: string;
  status: ClientFormStatus;
  submittedAt?: string;
  form: FormDefinition;
  answers: Answers;
}

export interface User {
  id: string;
  email: string;
  name?: string;
}

export interface AppConfig {
  emailEnabled: boolean;
  publicAppUrl: string;
  maxUploadMb: number;
}

export interface AnswerIssue {
  questionId: string;
  message: string;
}
